import type { ComponentChildren, JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../api";
import { copyToClipboard, enableNotifications, saveUserSettings, setRemoteContentDefault, toast } from "../actions";
import {
  isAdmin,
  passwordChangeEnabled,
  passwordMaxLength,
  passwordMinLength,
  remoteContentDefault,
  showSettings,
  userEmail,
  userSettings,
} from "../state";
import { prefs, setPref, type AfterMove, type Density, type Locale, type ReadingPane, type ThemePref } from "../prefs";
import { locale, t } from "../i18n";
import type { AccountKeyInfo, ApiTokenInfo, AuditEntry, RemoteContentDefault, RetentionPolicy, SoftDeleteEntry } from "../../shared/types";
import { sanitizeSignature } from "../util";
import { Icon, type IconName } from "./icons";
import { Dialog, Segmented, Spinner, Toggle } from "./kit";

type Section = "general" | "account" | "privacy" | "developer" | "admin";

/** Mox native account page shown when self-service change is disabled. */
const MOX_ACCOUNT_URL = "https://mail.gmhost.es/account/";

export function Settings(): JSX.Element | null {
  const [section, setSection] = useState<Section>("general");
  if (!showSettings.value) return null;
  const close = (): void => {
    showSettings.value = false;
  };

  const sections: Array<{ id: Section; label: string; icon: IconName }> = [
    { id: "general", label: t("settings.general"), icon: "sliders" },
    { id: "account", label: t("settings.account"), icon: "user" },
    { id: "privacy", label: t("settings.privacy"), icon: "shield" },
    { id: "developer", label: t("settings.developer"), icon: "code" },
    // Admin stays gated — never exposed to non-admins.
    ...(isAdmin.value ? [{ id: "admin" as const, label: t("settings.admin"), icon: "key" as const }] : []),
  ];

  return (
    <Dialog onClose={close} label={t("settings.title")} class="flex h-[min(760px,92vh)] max-w-4xl flex-col md:flex-row">
      <nav class="shrink-0 border-b border-line bg-canvas md:w-56 md:border-b-0 md:border-r">
        <div class="flex items-center justify-between px-5 pb-2 pt-4 md:pb-4">
          <h2 class="text-[15px] font-semibold text-fg">{t("settings.title")}</h2>
          <button class="icon-btn -mr-2 md:hidden" data-close onClick={close} aria-label={t("common.close")}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <ul class="flex gap-1 overflow-x-auto px-3 pb-3 md:flex-col md:px-2">
          {sections.map((s) => (
            <li key={s.id}>
              <button
                class={`flex h-9 w-full items-center gap-2.5 whitespace-nowrap rounded-[7px] px-2.5 text-[13.5px] transition-colors ${
                  section === s.id ? "bg-surface font-medium text-fg shadow-[0_0_0_1px_var(--color-line)]" : "text-fg-2 hover:bg-raised"
                }`}
                aria-current={section === s.id ? "page" : undefined}
                onClick={() => setSection(s.id)}
              >
                <Icon name={s.icon} size={16} class={section === s.id ? "text-accent-fg" : "text-fg-3"} />
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <div class="relative min-h-0 flex-1 overflow-y-auto">
        <button class="icon-btn absolute right-3 top-3 hidden md:inline-flex" data-close onClick={close} aria-label={t("common.close")}>
          <Icon name="x" size={18} />
        </button>
        <div class="mx-auto max-w-2xl px-5 py-6 md:px-8">
          {section === "general" && <GeneralSection />}
          {section === "account" && (
            <>
              <IdentityPanel />
              <PasswordPanel />
            </>
          )}
          {section === "privacy" && <RemoteContentPanel />}
          {section === "developer" && (
            <>
              <ApiTokensPanel />
              <AppPasswordsPanel />
            </>
          )}
          {section === "admin" && isAdmin.value && <AdminPanel />}
        </div>
      </div>
    </Dialog>
  );
}

/* ------------------------------- layout bits ------------------------------ */

function Panel({ title, description, children }: { title: string; description?: ComponentChildren; children: ComponentChildren }): JSX.Element {
  return (
    <section class="mb-10 last:mb-0">
      <h3 class="text-[15px] font-semibold text-fg">{title}</h3>
      {description && <p class="mt-1 text-[13px] leading-relaxed text-fg-3">{description}</p>}
      <div class="mt-4">{children}</div>
    </section>
  );
}

/** A labelled settings row: text on the left, control on the right. */
function Row({ label, help, children }: { label: string; help?: string; children: ComponentChildren }): JSX.Element {
  return (
    <div class="flex flex-col gap-2 border-b border-line py-3.5 first:pt-0 last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div class="min-w-0">
        <div class="text-[13.5px] text-fg">{label}</div>
        {help && <div class="mt-0.5 text-xs leading-relaxed text-fg-3">{help}</div>}
      </div>
      <div class="shrink-0">{children}</div>
    </div>
  );
}

function Notice({ tone, children }: { tone: "success" | "warn" | "error"; children: ComponentChildren }): JSX.Element {
  const cls = {
    success: "border-success/30 bg-success/8 text-success",
    warn: "border-warn/30 bg-warn/8 text-warn",
    error: "border-danger/30 bg-danger/8 text-danger",
  }[tone];
  return <div class={`rounded-[10px] border px-3.5 py-2.5 text-[13px] ${cls}`}>{children}</div>;
}

function SecretOnce({ value, label, onDismiss }: { value: string; label: string; onDismiss: () => void }): JSX.Element {
  return (
    <div class="mb-4 rounded-[10px] border border-success/30 bg-success/8 p-4">
      <p class="mb-2 text-[13px] font-medium text-success">{label}</p>
      <div class="flex items-center gap-2">
        <code class="min-w-0 flex-1 select-all break-all rounded-[6px] bg-surface px-2.5 py-2 font-mono text-xs text-fg">{value}</code>
        <button class="btn btn-secondary btn-sm" onClick={() => void copyToClipboard(value)}>
          <Icon name="copy" size={14} />
          {t("common.copy")}
        </button>
      </div>
      <button class="mt-2 text-xs text-fg-3 hover:text-fg" onClick={onDismiss}>
        {t("common.dismiss")}
      </button>
    </div>
  );
}

function fmtDate(ms: number): string {
  return new Date(ms).toLocaleDateString(locale.value);
}

/* --------------------------------- general -------------------------------- */

function GeneralSection(): JSX.Element {
  const p = prefs.value;
  return (
    <>
      <Panel title={t("settings.appearance")}>
        <Row label={t("settings.language")}>
          <Segmented<Locale>
            label={t("settings.language")}
            value={p.locale}
            onChange={(v) => setPref("locale", v)}
            options={[
              { value: "es", label: "Español" },
              { value: "en", label: "English" },
            ]}
          />
        </Row>
        <Row label={t("settings.theme")}>
          <Segmented<ThemePref>
            label={t("settings.theme")}
            value={p.theme}
            onChange={(v) => setPref("theme", v)}
            options={[
              { value: "light", label: t("theme.light"), icon: "sun" },
              { value: "dark", label: t("theme.dark"), icon: "moon" },
              { value: "system", label: t("theme.auto"), icon: "monitor" },
            ]}
          />
        </Row>
        <Row label={t("settings.density")} help={t("settings.densityHelp")}>
          <Segmented<Density>
            label={t("settings.density")}
            value={p.density}
            onChange={(v) => setPref("density", v)}
            options={[
              { value: "comfortable", label: t("settings.comfortable") },
              { value: "compact", label: t("settings.compact") },
            ]}
          />
        </Row>
        <Row label={t("settings.readingPane")} help={t("settings.readingPaneHelp")}>
          <Segmented<ReadingPane>
            label={t("settings.readingPane")}
            value={p.readingPane}
            onChange={(v) => setPref("readingPane", v)}
            options={[
              { value: "right", label: t("settings.paneRight") },
              { value: "off", label: t("settings.paneOff") },
            ]}
          />
        </Row>
        <Row label={t("settings.previews")} help={t("settings.previewsHelp")}>
          <Toggle label={t("settings.previews")} checked={p.showPreviews} onChange={(v) => setPref("showPreviews", v)} />
        </Row>
      </Panel>

      <Panel title={t("settings.behaviour")}>
        <Row label={t("settings.afterMove")} help={t("settings.afterMoveHelp")}>
          <Segmented<AfterMove>
            label={t("settings.afterMove")}
            value={p.afterMove}
            onChange={(v) => setPref("afterMove", v)}
            options={[
              { value: "next", label: t("settings.afterNext") },
              { value: "list", label: t("settings.afterList") },
            ]}
          />
        </Row>
        <Row label={t("settings.undoSend")} help={t("settings.undoSendHelp")}>
          <select
            class="field !w-auto"
            value={String(p.undoSendSeconds)}
            onChange={(e) => setPref("undoSendSeconds", Number((e.currentTarget as HTMLSelectElement).value))}
          >
            {[0, 5, 10, 20, 30].map((s) => (
              <option key={s} value={String(s)}>
                {s === 0 ? t("settings.undoOff") : t("settings.seconds", { n: s })}
              </option>
            ))}
          </select>
        </Row>
        <Row label={t("settings.notifications")} help={t("settings.notificationsHelp")}>
          <Toggle
            label={t("settings.notifications")}
            checked={p.notifications}
            onChange={async (v) => {
              if (v && !(await enableNotifications())) {
                toast(t("settings.notificationsDenied"), { tone: "error" });
                return;
              }
              setPref("notifications", v);
            }}
          />
        </Row>
      </Panel>
    </>
  );
}

/* ---------------------------- identity & signature ---------------------------- */

function IdentityPanel(): JSX.Element {
  const current = userSettings.value;
  const [name, setName] = useState(current.displayName);
  const [onReplies, setOnReplies] = useState(current.signatureOnReplies);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sigRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sigRef.current) sigRef.current.innerHTML = sanitizeSignature(current.signature);
  }, []);

  function fmt(cmd: string): void {
    sigRef.current?.focus();
    document.execCommand(cmd, false);
  }

  async function save(e: Event): Promise<void> {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const html = sigRef.current?.innerHTML ?? "";
      const plain = sigRef.current?.textContent?.trim() ?? "";
      await saveUserSettings({ displayName: name.trim(), signature: plain ? sanitizeSignature(html) : "", signatureOnReplies: onReplies });
      toast(t("settings.saved"), { tone: "success" });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("toast.actionFailed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel title={t("settings.identity")} description={t("settings.identityHelp", { email: userEmail.value })}>
      <form class="space-y-5" onSubmit={(e) => void save(e)}>
        <label class="block">
          <span class="field-label">{t("settings.displayName")}</span>
          <input class="field" value={name} maxLength={120} placeholder={userEmail.value.split("@")[0]} onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)} />
          <span class="mt-1.5 block text-xs text-fg-3">
            {t("settings.fromPreview")}: {name.trim() ? `${name.trim()} <${userEmail.value}>` : userEmail.value}
          </span>
        </label>
        <div>
          <span class="field-label">{t("settings.signature")}</span>
          <div class="overflow-hidden rounded-[var(--radius-control)] border border-line-strong focus-within:border-accent">
            <div class="flex gap-0.5 border-b border-line bg-canvas px-1.5 py-1">
              {(
                [
                  ["bold", "bold"],
                  ["italic", "italic"],
                  ["underline", "underline"],
                ] as const
              ).map(([cmd, icon]) => (
                <button key={cmd} type="button" class="icon-btn icon-btn-sm" aria-label={t(`format.${cmd}` as const)} onMouseDown={(e) => (e.preventDefault(), fmt(cmd))}>
                  <Icon name={icon} size={14} />
                </button>
              ))}
            </div>
            <div
              ref={sigRef}
              contentEditable
              role="textbox"
              aria-multiline="true"
              aria-label={t("settings.signature")}
              data-placeholder={t("settings.signaturePlaceholder")}
              class="rich-body min-h-[96px] bg-surface px-3 py-2.5 text-[13px]"
            />
          </div>
        </div>
        <label class="flex items-center justify-between gap-4 text-[13.5px] text-fg">
          <span>
            {t("settings.signatureOnReplies")}
            <span class="block text-xs text-fg-3">{t("settings.signatureOnRepliesHelp")}</span>
          </span>
          <Toggle label={t("settings.signatureOnReplies")} checked={onReplies} onChange={setOnReplies} />
        </label>
        {error && <Notice tone="error">{error}</Notice>}
        <button type="submit" class="btn btn-primary" disabled={saving}>
          {saving && <Spinner size={14} class="!border-on-accent/40 !border-t-on-accent" />}
          {t("common.saveChanges")}
        </button>
      </form>
    </Panel>
  );
}

/* --------------------------------- password -------------------------------- */

function PasswordPanel(): JSX.Element {
  const enabled = passwordChangeEnabled.value;
  // Policy bounds come from the server session — single source of truth.
  const minLength = passwordMinLength.value;
  const maxLength = passwordMaxLength.value;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Wipe plaintext passwords from component memory — on success AND on any
  // failed attempt, so nothing lingers after the form is submitted.
  function clearFields(): void {
    setCurrent("");
    setNext("");
    setConfirm("");
  }

  // Feature flag OFF → point users at Mox's native account page instead.
  if (!enabled) {
    return (
      <Panel title={t("password.title")} description={t("password.unavailable")}>
        <a class="btn btn-secondary" href={MOX_ACCOUNT_URL} target="_blank" rel="noopener noreferrer">
          {t("password.openAccountPage")}
          <Icon name="external" size={14} />
        </a>
      </Panel>
    );
  }

  async function handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!current || !next || !confirm) {
      setError(t("password.fillAll"));
      clearFields();
      return;
    }
    if (next.length < minLength || next.length > maxLength) {
      setError(t("password.length", { min: minLength, max: maxLength }));
      clearFields();
      return;
    }
    if (next !== confirm) {
      setError(t("password.mismatch"));
      clearFields();
      return;
    }
    if (next === current) {
      setError(t("password.same"));
      clearFields();
      return;
    }

    setSubmitting(true);
    try {
      await api.account.changePassword({ currentPassword: current, newPassword: next });
      setSuccess(true);
      clearFields();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("password.failed"));
      clearFields();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Panel title={t("password.title")} description={t("password.help", { min: minLength, max: maxLength })}>
      {success && (
        <div class="mb-4">
          <Notice tone="success">{t("password.changed")}</Notice>
        </div>
      )}
      <form onSubmit={(e) => void handleSubmit(e)} class="space-y-3.5">
        <label class="block">
          <span class="field-label">{t("password.current")}</span>
          <input class="field" type="password" autocomplete="current-password" value={current} onInput={(e) => setCurrent((e.currentTarget as HTMLInputElement).value)} required />
        </label>
        <div class="grid gap-3.5 sm:grid-cols-2">
          <label class="block">
            <span class="field-label">{t("password.new")}</span>
            <input
              class="field"
              type="password"
              autocomplete="new-password"
              minLength={minLength}
              maxLength={maxLength}
              value={next}
              onInput={(e) => setNext((e.currentTarget as HTMLInputElement).value)}
              required
            />
          </label>
          <label class="block">
            <span class="field-label">{t("password.confirm")}</span>
            <input
              class="field"
              type="password"
              autocomplete="new-password"
              minLength={minLength}
              maxLength={maxLength}
              value={confirm}
              onInput={(e) => setConfirm((e.currentTarget as HTMLInputElement).value)}
              required
            />
          </label>
        </div>
        {error && <Notice tone="error">{error}</Notice>}
        <button type="submit" class="btn btn-primary" disabled={submitting}>
          {submitting ? t("password.changing") : t("password.change")}
        </button>
      </form>
    </Panel>
  );
}

/* --------------------------------- privacy -------------------------------- */

function RemoteContentPanel(): JSX.Element {
  const current = remoteContentDefault.value;
  const [saving, setSaving] = useState<RemoteContentDefault | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSelect(value: RemoteContentDefault): Promise<void> {
    if (value === current || saving !== null) return;
    setError(null);
    setSaving(value);
    try {
      // The action re-throws non-auth failures (R4-001) so we render a local
      // error state instead of silently swallowing it, mirroring PasswordPanel.
      await setRemoteContentDefault(value);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("toast.actionFailed"));
    } finally {
      setSaving(null);
    }
  }

  const options: { value: RemoteContentDefault; label: string; description: string; icon: IconName }[] = [
    { value: "block", label: t("privacy.block"), description: t("privacy.blockHelp"), icon: "shield" },
    { value: "always", label: t("privacy.always"), description: t("privacy.alwaysHelp"), icon: "image" },
  ];

  return (
    <Panel title={t("privacy.title")} description={t("privacy.help")}>
      <div class="space-y-2" role="radiogroup" aria-label={t("privacy.title")}>
        {options.map((opt) => {
          const active = current === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={saving !== null}
              onClick={() => void handleSelect(opt.value)}
              class={`flex w-full items-start gap-3 rounded-[10px] border p-3.5 text-left transition-colors ${
                active ? "border-accent bg-accent/6" : "border-line hover:border-line-strong"
              }`}
            >
              <span class={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${active ? "border-accent" : "border-line-strong"}`}>
                {active && <span class="h-1.5 w-1.5 rounded-full bg-accent" />}
              </span>
              <span class="min-w-0 flex-1">
                <span class="flex items-center gap-2 text-[13.5px] font-medium text-fg">
                  {opt.label}
                  {saving === opt.value && <Spinner size={12} />}
                </span>
                <span class="mt-0.5 block text-xs leading-relaxed text-fg-3">{opt.description}</span>
              </span>
            </button>
          );
        })}
      </div>
      {error && (
        <div class="mt-3">
          <Notice tone="error">{error}</Notice>
        </div>
      )}
    </Panel>
  );
}

/* -------------------------------- developer -------------------------------- */

function ScopeBadge({ scope }: { scope: string }): JSX.Element {
  const cls = scope === "full" ? "bg-danger/10 text-danger" : scope === "send" ? "bg-warn/10 text-warn" : "bg-raised text-fg-3";
  return <span class={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{scope}</span>;
}

function RevokedBadge(): JSX.Element {
  return <span class="shrink-0 rounded-full bg-danger/10 px-2 py-0.5 text-[11px] font-medium text-danger">{t("dev.revoked")}</span>;
}

function ApiTokensPanel(): JSX.Element {
  const [tokens, setTokens] = useState<ApiTokenInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [newToken, setNewToken] = useState<string | null>(null);

  // Create form state
  const [label, setLabel] = useState("");
  const [scope, setScope] = useState<"read" | "send" | "full">("read");
  const [expiryDays, setExpiryDays] = useState("");
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    void api.tokens
      .list()
      .then(setTokens)
      .catch((e: unknown) => setFetchError(e instanceof Error ? e.message : "Failed to load tokens"))
      .finally(() => setLoading(false));
  }, []);

  async function handleCreate(e: Event): Promise<void> {
    e.preventDefault();
    if (!label.trim()) return;
    setCreating(true);
    setFormError(null);
    try {
      const result = await api.tokens.create({
        label: label.trim(),
        scope,
        expiresInDays: expiryDays ? Number(expiryDays) : undefined,
      });
      setNewToken(result.token);
      setLabel("");
      setExpiryDays("");
      setTokens(await api.tokens.list());
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : "Failed to create token");
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: number): Promise<void> {
    try {
      await api.tokens.revoke(id);
      setTokens((tk) => tk.filter((tok) => tok.id !== id));
    } catch (e: unknown) {
      setFetchError(e instanceof Error ? e.message : "Failed to revoke token");
    }
  }

  return (
    <Panel
      title={t("dev.tokens")}
      description={
        <>
          {t("dev.tokensHelp")} <code class="rounded bg-raised px-1 font-mono text-xs">Authorization: Bearer twm_…</code>{" "}
          <a class="text-accent-fg underline" href="/api/docs" target="_blank" rel="noopener">
            {t("dev.apiDocs")}
          </a>
        </>
      }
    >
      {newToken && <SecretOnce value={newToken} label={t("dev.tokenCreated")} onDismiss={() => setNewToken(null)} />}

      <form onSubmit={(e) => void handleCreate(e)} class="grid gap-3 rounded-[10px] border border-line p-4 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <label class="block">
          <span class="field-label">{t("dev.label")}</span>
          <input class="field" placeholder={t("dev.tokenLabelPlaceholder")} value={label} onInput={(e) => setLabel((e.currentTarget as HTMLInputElement).value)} required />
        </label>
        <label class="block">
          <span class="field-label">{t("dev.scope")}</span>
          <select class="field" value={scope} onChange={(e) => setScope((e.currentTarget as HTMLSelectElement).value as typeof scope)}>
            <option value="read">{t("dev.scopeRead")}</option>
            <option value="send">{t("dev.scopeSend")}</option>
            <option value="full">{t("dev.scopeFull")}</option>
          </select>
        </label>
        <label class="block sm:w-32">
          <span class="field-label">{t("dev.expires")}</span>
          <input class="field" placeholder={t("dev.never")} type="number" min="1" value={expiryDays} onInput={(e) => setExpiryDays((e.currentTarget as HTMLInputElement).value)} />
        </label>
        <button type="submit" class="btn btn-primary sm:col-span-3 sm:justify-self-start" disabled={creating}>
          {creating ? t("dev.creating") : t("dev.createToken")}
        </button>
      </form>

      {formError && <div class="mt-3"><Notice tone="error">{formError}</Notice></div>}
      {fetchError && <div class="mt-3"><Notice tone="error">{fetchError}</Notice></div>}

      <div class="mt-4">
        {loading ? (
          <Spinner />
        ) : tokens.length === 0 ? (
          <p class="text-[13px] text-fg-3">{t("dev.noTokens")}</p>
        ) : (
          <ul class="divide-y divide-line rounded-[10px] border border-line">
            {tokens.map((tok) => (
              <li key={tok.id} class="flex items-center gap-3 px-4 py-3">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <span class="truncate text-[13.5px] font-medium text-fg">{tok.label}</span>
                    <ScopeBadge scope={tok.scope} />
                    {tok.revoked && <RevokedBadge />}
                  </div>
                  <div class="mt-0.5 text-xs text-fg-3">
                    {t("dev.created", { date: fmtDate(tok.createdAt) })}
                    {tok.lastUsedAt ? ` · ${t("dev.lastUsed", { date: fmtDate(tok.lastUsedAt) })}` : ""}
                    {tok.expiresAt ? ` · ${t("dev.expiresOn", { date: fmtDate(tok.expiresAt) })}` : ""}
                  </div>
                </div>
                {!tok.revoked && (
                  <button class="btn btn-ghost btn-sm !text-danger" onClick={() => void handleRevoke(tok.id)}>
                    {t("dev.revoke")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}

function AppPasswordsPanel(): JSX.Element {
  const [keys, setKeys] = useState<AccountKeyInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);

  const [label, setLabel] = useState("");
  const [expiryDays, setExpiryDays] = useState("");
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    void api.accountKeys
      .list()
      .then(setKeys)
      .catch((e: unknown) => setFetchError(e instanceof Error ? e.message : "Failed to load app passwords"))
      .finally(() => setLoading(false));
  }, []);

  async function handleCreate(e: Event): Promise<void> {
    e.preventDefault();
    if (!label.trim()) return;
    setCreating(true);
    setFormError(null);
    try {
      const result = await api.accountKeys.create({
        label: label.trim(),
        expiresInDays: expiryDays ? Number(expiryDays) : undefined,
      });
      setNewKey(result.key);
      setLabel("");
      setExpiryDays("");
      setKeys(await api.accountKeys.list());
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : "Failed to create app password");
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: number): Promise<void> {
    try {
      await api.accountKeys.revoke(id);
      setKeys((k) => k.filter((key) => key.id !== id));
    } catch (e: unknown) {
      setFetchError(e instanceof Error ? e.message : "Failed to revoke app password");
    }
  }

  return (
    <Panel title={t("dev.appPasswords")} description={t("dev.appPasswordsHelp")}>
      <div class="mb-4">
        <Notice tone="warn">{t("dev.appPasswordsPreview")}</Notice>
      </div>
      {newKey && <SecretOnce value={newKey} label={t("dev.appPasswordCreated")} onDismiss={() => setNewKey(null)} />}

      <form onSubmit={(e) => void handleCreate(e)} class="grid gap-3 rounded-[10px] border border-line p-4 sm:grid-cols-[1fr_auto] sm:items-end">
        <label class="block">
          <span class="field-label">{t("dev.label")}</span>
          <input class="field" placeholder={t("dev.appLabelPlaceholder")} value={label} onInput={(e) => setLabel((e.currentTarget as HTMLInputElement).value)} required />
        </label>
        <label class="block sm:w-32">
          <span class="field-label">{t("dev.expires")}</span>
          <input class="field" placeholder={t("dev.never")} type="number" min="1" value={expiryDays} onInput={(e) => setExpiryDays((e.currentTarget as HTMLInputElement).value)} />
        </label>
        <button type="submit" class="btn btn-primary sm:col-span-2 sm:justify-self-start" disabled={creating}>
          {creating ? t("dev.creating") : t("dev.createAppPassword")}
        </button>
      </form>

      {formError && <div class="mt-3"><Notice tone="error">{formError}</Notice></div>}
      {fetchError && <div class="mt-3"><Notice tone="error">{fetchError}</Notice></div>}

      <div class="mt-4">
        {loading ? (
          <Spinner />
        ) : keys.length === 0 ? (
          <p class="text-[13px] text-fg-3">{t("dev.noAppPasswords")}</p>
        ) : (
          <ul class="divide-y divide-line rounded-[10px] border border-line">
            {keys.map((k) => (
              <li key={k.id} class="flex items-center gap-3 px-4 py-3">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <span class="truncate text-[13.5px] font-medium text-fg">{k.label}</span>
                    {k.revoked && <RevokedBadge />}
                  </div>
                  <div class="mt-0.5 text-xs text-fg-3">
                    {t("dev.created", { date: fmtDate(k.createdAt) })}
                    {k.expiresAt ? ` · ${t("dev.expiresOn", { date: fmtDate(k.expiresAt) })}` : ""}
                  </div>
                </div>
                {!k.revoked && (
                  <button class="btn btn-ghost btn-sm !text-danger" onClick={() => void handleRevoke(k.id)}>
                    {t("dev.revoke")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}

/* ---------------------------------- admin --------------------------------- */

interface AdminUser {
  email: string;
  localpart: string;
}

interface AdminToken {
  id: number;
  label: string;
  email: string;
  scope: string;
  createdAt: number;
  lastUsedAt: number | null;
  expiresAt: number | null;
  revoked: boolean;
}

function Table({ head, children }: { head: string[]; children: ComponentChildren }): JSX.Element {
  return (
    <div class="overflow-x-auto rounded-[10px] border border-line">
      <table class="w-full text-xs">
        <thead>
          <tr class="border-b border-line bg-canvas">
            {head.map((h) => (
              <th key={h} class="whitespace-nowrap px-3 py-2 text-left font-medium text-fg-3">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody class="divide-y divide-line">{children}</tbody>
      </table>
    </div>
  );
}

function Loadable({ loading, error, empty, children }: { loading: boolean; error: string | null; empty: string | null; children: ComponentChildren }): JSX.Element {
  if (loading) return <Spinner />;
  return (
    <>
      {error && <Notice tone="error">{error}</Notice>}
      {empty ? <p class="text-[13px] text-fg-3">{empty}</p> : children}
    </>
  );
}

function AdminPanel(): JSX.Element {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [tokens, setTokens] = useState<AdminToken[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [tokensError, setTokensError] = useState<string | null>(null);

  // Audit log state
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [loadingAudit, setLoadingAudit] = useState(true);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [auditFilter, setAuditFilter] = useState("");

  // Soft deletes state
  const [softDeletes, setSoftDeletes] = useState<SoftDeleteEntry[]>([]);
  const [loadingSoftDeletes, setLoadingSoftDeletes] = useState(true);
  const [softDeletesError, setSoftDeletesError] = useState<string | null>(null);

  // Retention policy state
  const [policies, setPolicies] = useState<RetentionPolicy[]>([]);
  const [loadingPolicies, setLoadingPolicies] = useState(true);
  const [policiesError, setPoliciesError] = useState<string | null>(null);
  const [newMailboxPath, setNewMailboxPath] = useState("");
  const [newMaxAgeDays, setNewMaxAgeDays] = useState("");
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [policyFormError, setPolicyFormError] = useState<string | null>(null);

  useEffect(() => {
    void api.admin
      .users()
      .then(setUsers)
      .catch((e: unknown) => setUsersError(e instanceof Error ? e.message : "Failed to load users"))
      .finally(() => setLoadingUsers(false));

    void api.admin
      .tokens()
      .then(setTokens)
      .catch((e: unknown) => setTokensError(e instanceof Error ? e.message : "Failed to load tokens"))
      .finally(() => setLoadingTokens(false));

    void api.admin
      .audit(200)
      .then((res) => {
        setAuditEntries(res.entries);
        setAuditTotal(res.total);
      })
      .catch((e: unknown) => setAuditError(e instanceof Error ? e.message : "Failed to load audit log"))
      .finally(() => setLoadingAudit(false));

    void api.admin
      .softDeletes(100)
      .then(setSoftDeletes)
      .catch((e: unknown) => setSoftDeletesError(e instanceof Error ? e.message : "Failed to load soft deletes"))
      .finally(() => setLoadingSoftDeletes(false));

    void api.admin.retention
      .list()
      .then(setPolicies)
      .catch((e: unknown) => setPoliciesError(e instanceof Error ? e.message : "Failed to load retention policies"))
      .finally(() => setLoadingPolicies(false));
  }, []);

  async function handleAddPolicy(e: Event): Promise<void> {
    e.preventDefault();
    const days = Number(newMaxAgeDays);
    if (!newMailboxPath.trim() || !days || days < 1) return;
    setSavingPolicy(true);
    setPolicyFormError(null);
    try {
      const updated = await api.admin.retention.upsert({ mailboxPath: newMailboxPath.trim(), maxAgeDays: days });
      setPolicies(updated);
      setNewMailboxPath("");
      setNewMaxAgeDays("");
    } catch (e: unknown) {
      setPolicyFormError(e instanceof Error ? e.message : "Failed to save policy");
    } finally {
      setSavingPolicy(false);
    }
  }

  async function handleDeletePolicy(mailboxPath: string): Promise<void> {
    try {
      await api.admin.retention.delete(mailboxPath);
      setPolicies((p) => p.filter((pol) => pol.mailboxPath !== mailboxPath));
    } catch (e: unknown) {
      setPoliciesError(e instanceof Error ? e.message : "Failed to delete policy");
    }
  }

  const af = auditFilter.trim().toLowerCase();
  const audit = af
    ? auditEntries.filter((x) => `${x.actor} ${x.action} ${x.detail ?? ""} ${x.ip ?? ""}`.toLowerCase().includes(af))
    : auditEntries;

  return (
    <>
      <Panel title={t("admin.users")} description={t("admin.usersHelp", { n: users.length })}>
        <Loadable loading={loadingUsers} error={usersError} empty={users.length === 0 ? t("admin.noUsers") : null}>
          <Table head={[t("admin.email"), t("admin.localpart")]}>
            {users.map((u) => (
              <tr key={u.email}>
                <td class="px-3 py-2 text-fg">{u.email}</td>
                <td class="px-3 py-2 text-fg-3">{u.localpart}</td>
              </tr>
            ))}
          </Table>
        </Loadable>
      </Panel>

      <Panel title={t("admin.allTokens")}>
        <Loadable loading={loadingTokens} error={tokensError} empty={tokens.length === 0 ? t("dev.noTokens") : null}>
          <ul class="divide-y divide-line rounded-[10px] border border-line">
            {tokens.map((tok) => (
              <li key={tok.id} class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <span class="truncate text-[13.5px] font-medium text-fg">{tok.label}</span>
                  <ScopeBadge scope={tok.scope} />
                  {tok.revoked && <RevokedBadge />}
                </div>
                <div class="mt-0.5 text-xs text-fg-3">
                  <span class="text-fg-2">{tok.email}</span> · {t("dev.created", { date: fmtDate(tok.createdAt) })}
                  {tok.lastUsedAt ? ` · ${t("dev.lastUsed", { date: fmtDate(tok.lastUsedAt) })}` : ""}
                </div>
              </li>
            ))}
          </ul>
        </Loadable>
      </Panel>

      <Panel title={t("admin.audit")} description={auditTotal > 0 ? t("admin.auditTotal", { n: auditTotal }) : undefined}>
        <input class="field mb-3" placeholder={t("admin.auditFilter")} value={auditFilter} onInput={(e) => setAuditFilter((e.currentTarget as HTMLInputElement).value)} />
        <Loadable loading={loadingAudit} error={auditError} empty={audit.length === 0 ? t("admin.noAudit") : null}>
          <Table head={[t("admin.time"), t("admin.actor"), t("admin.action"), t("admin.detail"), "IP"]}>
            {audit.map((entry) => (
              <tr key={entry.id}>
                <td class="whitespace-nowrap px-3 py-2 text-fg-3">{new Date(entry.ts).toLocaleString(locale.value)}</td>
                <td class="px-3 py-2 text-fg">{entry.actor}</td>
                <td class="px-3 py-2">
                  <span class="rounded bg-raised px-1.5 py-0.5 font-mono text-fg">{entry.action}</span>
                </td>
                <td class="px-3 py-2 text-fg-3">{entry.detail ?? "—"}</td>
                <td class="px-3 py-2 text-fg-3">{entry.ip ?? "—"}</td>
              </tr>
            ))}
          </Table>
        </Loadable>
      </Panel>

      <Panel title={t("admin.softDeletes")}>
        <Loadable loading={loadingSoftDeletes} error={softDeletesError} empty={softDeletes.length === 0 ? t("admin.noSoftDeletes") : null}>
          <Table head={[t("admin.email"), t("admin.mailbox"), "UID", t("compose.subject"), t("admin.deletedAt"), t("admin.expunged")]}>
            {softDeletes.map((sd) => (
              <tr key={sd.id}>
                <td class="px-3 py-2 text-fg">{sd.email}</td>
                <td class="px-3 py-2 text-fg-3">{sd.mailbox}</td>
                <td class="px-3 py-2 font-mono text-fg-3">{sd.uid}</td>
                <td class="px-3 py-2 text-fg-3">{sd.subject ?? "—"}</td>
                <td class="whitespace-nowrap px-3 py-2 text-fg-3">{new Date(sd.deletedAt).toLocaleString(locale.value)}</td>
                <td class="px-3 py-2">{sd.expunged ? <span class="text-danger">{t("common.yes")}</span> : <span class="text-fg-3">{t("common.no")}</span>}</td>
              </tr>
            ))}
          </Table>
        </Loadable>
      </Panel>

      <Panel title={t("admin.retention")} description={t("admin.retentionHelp")}>
        <form onSubmit={(e) => void handleAddPolicy(e)} class="mb-3 flex flex-wrap gap-2">
          <input class="field min-w-[160px] flex-1" placeholder={t("admin.mailboxPath")} value={newMailboxPath} onInput={(e) => setNewMailboxPath((e.currentTarget as HTMLInputElement).value)} required />
          <input class="field !w-36" placeholder={t("admin.maxAge")} type="number" min="1" value={newMaxAgeDays} onInput={(e) => setNewMaxAgeDays((e.currentTarget as HTMLInputElement).value)} required />
          <button type="submit" class="btn btn-primary" disabled={savingPolicy}>
            {savingPolicy ? t("common.saving") : t("admin.addPolicy")}
          </button>
        </form>
        {policyFormError && <div class="mb-3"><Notice tone="error">{policyFormError}</Notice></div>}
        <Loadable loading={loadingPolicies} error={policiesError} empty={policies.length === 0 ? t("admin.noPolicies") : null}>
          <Table head={[t("admin.mailboxPath"), t("admin.maxAge"), t("admin.created"), ""]}>
            {policies.map((pol) => (
              <tr key={pol.id}>
                <td class="px-3 py-2 font-mono text-fg">{pol.mailboxPath}</td>
                <td class="px-3 py-2 text-fg-3">{pol.maxAgeDays}</td>
                <td class="whitespace-nowrap px-3 py-2 text-fg-3">{fmtDate(pol.createdAt)}</td>
                <td class="px-3 py-2 text-right">
                  <button class="btn btn-ghost btn-sm !text-danger" onClick={() => void handleDeletePolicy(pol.mailboxPath)}>
                    {t("common.remove")}
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        </Loadable>
      </Panel>
    </>
  );
}
